import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash2, MapPin, Save, X, Edit, Users } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { Badge } from '../ui/badge';
import { MapContainer, TileLayer, Polygon, Marker, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const pointIcon = new L.DivIcon({
  className: 'wz-point-icon',
  html: '<div style="width:14px;height:14px;border-radius:50%;background:#ef4444;border:2px solid #fff;box-shadow:0 0 4px rgba(0,0,0,0.4);"></div>',
  iconSize: [14, 14],
  iconAnchor: [7, 7],
});

const deletePointIcon = new L.DivIcon({
  className: 'wz-delete-point-icon',
  html: '<div style="width:18px;height:18px;border-radius:50%;background:#dc2626;border:3px solid #fff;box-shadow:0 0 6px rgba(220,38,38,0.6);cursor:pointer;display:flex;align-items:center;justify-content:center;"><span style="color:#fff;font-size:10px;font-weight:bold;line-height:1;">×</span></div>',
  iconSize: [18, 18],
  iconAnchor: [9, 9],
});

const testPointIcon = new L.DivIcon({
  className: 'wz-test-icon',
  html: '<div style="width:18px;height:18px;border-radius:50%;background:#3b82f6;border:3px solid #fff;box-shadow:0 0 6px rgba(0,0,0,0.5);"></div>',
  iconSize: [18, 18],
  iconAnchor: [9, 9],
});

// Component for handling map clicks
function MapClickHandler({ onMapClick, drawing }) {
  useMapEvents({
    click(e) {
      if (drawing) {
        onMapClick(e.latlng);
      }
    },
  });
  return null;
}

function FitToPolygon({ polygon }) {
  const map = useMap();
  useEffect(() => {
    if (polygon && polygon.length >= 3) {
      const bounds = L.latLngBounds(polygon.map(p => [p.lat, p.lng]));
      map.fitBounds(bounds, { padding: [40, 40] });
    }
  }, [polygon, map]);
  return null;
}

// Ray-casting point-in-polygon (matches backend logic)
// lat = Y axis, lng = X axis. Point (lng=x, lat=y)
function pointInPolygon(lat, lng, polygon) {
  if (!polygon || polygon.length < 3) return false;
  let inside = false;
  let j = polygon.length - 1;
  for (let i = 0; i < polygon.length; i++) {
    const yi = polygon[i].lat, xi = polygon[i].lng;
    const yj = polygon[j].lat, xj = polygon[j].lng;
    const intersect = ((yi > lat) !== (yj > lat)) &&
      (lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi);
    if (intersect) inside = !inside;
    j = i;
  }
  return inside;
}

const DEFAULT_CENTER = [40.4093, 49.8671]; // Baku

export default function WorkZonesPanel() {
  const token = localStorage.getItem('token');
  const [zones, setZones] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  // Dialog state for create/edit
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingZone, setEditingZone] = useState(null); // null = create, object = edit
  const [zoneName, setZoneName] = useState('');
  const [zoneGroupId, setZoneGroupId] = useState('');
  const [zonePolygon, setZonePolygon] = useState([]);
  const [zoneTechnicians, setZoneTechnicians] = useState([]);
  const [drawing, setDrawing] = useState(true);
  const [deleteMode, setDeleteMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]); // {type:'add', point} | {type:'delete', index, point}

  // Test mode (point-in-polygon)
  const [testMode, setTestMode] = useState(false);
  const [testPoint, setTestPoint] = useState(null);
  const [testResult, setTestResult] = useState(null);

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      const [zonesRes, techRes, groupsRes] = await Promise.all([
        axios.get(`${API}/work-zones`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/technicians`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/groups`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setZones(zonesRes.data || []);
      setTechnicians(techRes.data || []);
      setGroups(groupsRes.data || []);
    } catch (e) {
      toast.error('Məlumatlar yüklənmədi');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openCreate = () => {
    setEditingZone(null);
    setZoneName('');
    setZoneGroupId('');
    setZonePolygon([]);
    setZoneTechnicians([]);
    setDrawing(true);
    setDeleteMode(false);
    setTestMode(false);
    setTestPoint(null);
    setTestResult(null);
    setHistory([]);
    setDialogOpen(true);
  };

  const openEdit = (zone) => {
    setEditingZone(zone);
    setZoneName(zone.name || '');
    setZoneGroupId(zone.group_id || '');
    setZonePolygon(zone.polygon || []);
    setZoneTechnicians(zone.assigned_technicians || []);
    setDrawing(false);
    setDeleteMode(false);
    setTestMode(false);
    setTestPoint(null);
    setTestResult(null);
    setHistory([]);
    setDialogOpen(true);
  };

  const handleMapClick = (latlng) => {
    if (testMode) {
      const pt = { lat: latlng.lat, lng: latlng.lng };
      setTestPoint(pt);
      setTestResult(pointInPolygon(pt.lat, pt.lng, zonePolygon));
    } else if (drawing && !deleteMode) {
      const point = { lat: latlng.lat, lng: latlng.lng };
      setZonePolygon(prev => [...prev, point]);
      setHistory(prev => [...prev, { type: 'add', point }]);
    }
  };

  const handlePointClick = (idx) => {
    if (deleteMode) {
      const removed = zonePolygon[idx];
      setZonePolygon(prev => prev.filter((_, i) => i !== idx));
      setHistory(prev => [...prev, { type: 'delete', index: idx, point: removed }]);
    }
  };

  const undoLastAction = () => {
    if (history.length === 0) return;
    const last = history[history.length - 1];
    if (last.type === 'add') {
      // Çəkilmiş nöqtəni geri qaytar = sil
      setZonePolygon(prev => {
        // Dəqiq həmin nöqtəni axırdan sil (eyni koordinatlı dublikat ola bilər - axırıncını sil)
        let idx = -1;
        for (let i = prev.length - 1; i >= 0; i--) {
          if (prev[i].lat === last.point.lat && prev[i].lng === last.point.lng) { idx = i; break; }
        }
        if (idx === -1) return prev.slice(0, -1);
        return prev.filter((_, i) => i !== idx);
      });
    } else if (last.type === 'delete') {
      // Silinən nöqtəni öz yerinə qaytar
      setZonePolygon(prev => {
        const next = [...prev];
        const insertAt = Math.min(last.index, next.length);
        next.splice(insertAt, 0, last.point);
        return next;
      });
    }
    setHistory(prev => prev.slice(0, -1));
  };

  const clearPolygon = () => {
    setZonePolygon([]);
    setHistory([]);
    setTestPoint(null);
    setTestResult(null);
  };

  const toggleTechnician = (techId) => {
    setZoneTechnicians(prev =>
      prev.includes(techId) ? prev.filter(id => id !== techId) : [...prev, techId]
    );
  };

  const handleSave = async () => {
    if (!zoneName.trim()) {
      toast.error('Ərazi adı daxil edin');
      return;
    }
    if (zonePolygon.length < 3) {
      toast.error('Ən azı 3 nöqtə əlavə edin (poligon yaratmaq üçün)');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: zoneName.trim(),
        group_id: zoneGroupId || null,
        polygon: zonePolygon,
        assigned_technicians: zoneTechnicians,
      };
      if (editingZone) {
        await axios.put(`${API}/work-zones/${editingZone.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Ərazi yeniləndi');
      } else {
        await axios.post(`${API}/work-zones`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Ərazi yaradıldı');
      }
      setDialogOpen(false);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Yadda saxlamaq mümkün olmadı');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (zone) => {
    if (!window.confirm(`"${zone.name}" ərazisini silmək istəyirsiniz?`)) return;
    try {
      await axios.delete(`${API}/work-zones/${zone.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Ərazi silindi');
      fetchAll();
    } catch (e) {
      toast.error('Silmək mümkün olmadı');
    }
  };

  const techMap = Object.fromEntries(technicians.map(t => [t.id, t.full_name || t.username]));
  const groupMap = Object.fromEntries(groups.map(g => [g.id, g.name]));

  // Qrup seçilibsə yalnız həmin qrupun texniklərini göstər
  const filteredTechnicians = zoneGroupId
    ? technicians.filter(t => Array.isArray(t.groups) && t.groups.includes(zoneGroupId))
    : technicians;

  return (
    <div className="space-y-6" data-testid="work-zones-panel">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5 text-teal-600" />
              İş Əraziləri (Geofencing)
            </CardTitle>
            <p className="text-sm text-zinc-500 mt-1">
              Poligon əsaslı ərazilər təyin edin və texnikləri biriktirin. Texnikin GPS koordinatı yalnız öz ərazisindədirsə işə başlaya bilər.
            </p>
          </div>
          <Button onClick={openCreate} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="create-zone-btn">
            <Plus className="h-4 w-4 mr-2" />
            Yeni Ərazi
          </Button>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-zinc-500 text-center py-8">Yüklənir...</p>
          ) : zones.length === 0 ? (
            <div className="text-center py-12 text-zinc-500">
              <MapPin className="h-12 w-12 mx-auto mb-3 text-zinc-300" />
              <p>Heç bir ərazi əlavə edilməyib</p>
              <p className="text-xs mt-1">"Yeni Ərazi" düyməsi ilə başlayın</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {zones.map(zone => (
                <Card key={zone.id} className="border-2 hover:border-teal-400 transition" data-testid={`zone-card-${zone.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-semibold text-zinc-900">{zone.name}</h3>
                        {zone.group_id && (
                          <p className="text-xs text-zinc-500 mt-1">Qrup: {groupMap[zone.group_id] || zone.group_id}</p>
                        )}
                      </div>
                      <Badge variant="outline" className="bg-teal-50 text-teal-700 border-teal-200">
                        {zone.polygon?.length || 0} nöqtə
                      </Badge>
                    </div>
                    <div className="mb-3">
                      <p className="text-xs text-zinc-500 mb-1 flex items-center gap-1">
                        <Users className="h-3 w-3" />
                        Təyin olunan texniklər ({zone.assigned_technicians?.length || 0})
                      </p>
                      <div className="flex flex-wrap gap-1">
                        {(zone.assigned_technicians || []).slice(0, 4).map(tid => (
                          <Badge key={tid} variant="secondary" className="text-xs">
                            {techMap[tid] || tid.slice(0, 6)}
                          </Badge>
                        ))}
                        {(zone.assigned_technicians?.length || 0) > 4 && (
                          <Badge variant="secondary" className="text-xs">
                            +{zone.assigned_technicians.length - 4}
                          </Badge>
                        )}
                        {(!zone.assigned_technicians || zone.assigned_technicians.length === 0) && (
                          <span className="text-xs text-zinc-400 italic">Heç kim təyin edilməyib</span>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openEdit(zone)}
                        className="flex-1"
                        data-testid={`edit-zone-${zone.id}`}
                      >
                        <Edit className="h-3 w-3 mr-1" />
                        Redaktə
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDelete(zone)}
                        className="text-red-600 border-red-200 hover:bg-red-50"
                        data-testid={`delete-zone-${zone.id}`}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingZone ? 'Ərazini Redaktə Et' : 'Yeni Ərazi Yarat'}</DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Left: form fields */}
            <div className="space-y-4 lg:col-span-1">
              <div>
                <label className="text-sm font-medium text-zinc-700 mb-1 block">Ərazi adı *</label>
                <Input
                  value={zoneName}
                  onChange={(e) => setZoneName(e.target.value)}
                  placeholder="Məs: Bakı Mərkəz"
                  data-testid="zone-name-input"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-zinc-700 mb-1 block">Qrup (istəyə bağlı)</label>
                <Select
                  value={zoneGroupId || 'none'}
                  onValueChange={(v) => {
                    const newGroup = v === 'none' ? '' : v;
                    setZoneGroupId(newGroup);
                    // Yeni qrupda olmayan texnikləri seçimdən çıxar
                    if (newGroup) {
                      setZoneTechnicians(prev => prev.filter(tid => {
                        const t = technicians.find(x => x.id === tid);
                        return t && Array.isArray(t.groups) && t.groups.includes(newGroup);
                      }));
                    }
                  }}
                >
                  <SelectTrigger data-testid="zone-group-select">
                    <SelectValue placeholder="Qrup seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Yoxdur (bütün texniklər) —</SelectItem>
                    {groups.map(g => (
                      <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm font-medium text-zinc-700 mb-2 block">
                  Texniklər ({zoneTechnicians.length} seçilib{zoneGroupId ? ` · ${filteredTechnicians.length} göstərilir` : ''})
                </label>
                <div className="border rounded-md max-h-48 overflow-y-auto p-2 space-y-1" data-testid="technicians-list">
                  {filteredTechnicians.length === 0 && (
                    <p className="text-xs text-zinc-400 italic p-2">
                      {zoneGroupId ? 'Bu qrupda texnik tapılmadı' : 'Texnik tapılmadı'}
                    </p>
                  )}
                  {filteredTechnicians.map(t => (
                    <label key={t.id} className="flex items-center gap-2 cursor-pointer hover:bg-zinc-50 p-1 rounded">
                      <Checkbox
                        checked={zoneTechnicians.includes(t.id)}
                        onCheckedChange={() => toggleTechnician(t.id)}
                        data-testid={`tech-checkbox-${t.id}`}
                      />
                      <span className="text-sm">{t.full_name || t.username}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="bg-zinc-50 border border-zinc-200 rounded-md p-3 space-y-2">
                <p className="text-xs font-medium text-zinc-700">
                  Poligon: {zonePolygon.length} nöqtə
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant={drawing && !testMode && !deleteMode ? 'default' : 'outline'}
                    onClick={() => { setDrawing(true); setTestMode(false); setDeleteMode(false); }}
                    className={drawing && !testMode && !deleteMode ? 'bg-teal-600 text-white' : ''}
                    data-testid="draw-mode-btn"
                  >
                    Çək
                  </Button>
                  <Button
                    size="sm"
                    variant={deleteMode ? 'default' : 'outline'}
                    onClick={() => { setDeleteMode(!deleteMode); setTestMode(false); setDrawing(false); }}
                    disabled={zonePolygon.length === 0}
                    className={deleteMode ? 'bg-red-600 text-white' : 'text-red-600'}
                    data-testid="delete-mode-btn"
                  >
                    Sil
                  </Button>
                  <Button size="sm" variant="outline" onClick={undoLastAction} disabled={history.length === 0} data-testid="undo-point-btn">
                    Geri al
                  </Button>
                  <Button size="sm" variant="outline" onClick={clearPolygon} disabled={zonePolygon.length === 0} className="text-red-600">
                    Təmizlə
                  </Button>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant={testMode ? 'default' : 'outline'}
                    onClick={() => { setTestMode(!testMode); setDeleteMode(false); setDrawing(!testMode ? false : true); setTestPoint(null); setTestResult(null); }}
                    disabled={zonePolygon.length < 3}
                    className={testMode ? 'bg-blue-600 text-white' : ''}
                    data-testid="test-mode-btn"
                  >
                    {testMode ? '✓ Test rejimi (aktiv)' : 'Nöqtə Testi'}
                  </Button>
                </div>
                {testMode && (
                  <div className="text-xs">
                    <p className="text-zinc-600">
                      Bu nə işə yarayır? — Xəritədə hər hansı GPS nöqtəsinə kliklə, həmin nöqtənin
                      bu ərazinin <strong>daxilində</strong> və ya <strong>kənarında</strong> olduğunu
                      yoxlayın. Texnikin GPS koordinatı bu zonaya düşürmü əvvəlcədən test edə bilərsiniz.
                    </p>
                    {testPoint && (
                      <div className="mt-2 p-2 rounded" style={{
                        backgroundColor: testResult ? '#dcfce7' : '#fee2e2',
                        color: testResult ? '#166534' : '#991b1b',
                      }}>
                        <p className="font-medium">
                          {testResult ? '✓ Daxildədir' : '✗ Kənardadır'}
                        </p>
                        <p className="font-mono text-[11px] mt-1">
                          {testPoint.lat.toFixed(5)}, {testPoint.lng.toFixed(5)}
                        </p>
                      </div>
                    )}
                  </div>
                )}
                {deleteMode && (
                  <p className="text-xs text-red-600">
                    🗑️ Silmək istədiyiniz nöqtənin üstünə xəritədə kliklə.
                  </p>
                )}
                {!deleteMode && !testMode && (
                  <p className="text-[11px] text-zinc-500 leading-relaxed">
                    💡 Çəkmə rejimində xəritədə kliklə nöqtə əlavə et. Ən azı 3 nöqtə.
                  </p>
                )}
              </div>
            </div>

            {/* Right: map */}
            <div className="lg:col-span-2">
              <div className="border-2 border-zinc-200 rounded-lg overflow-hidden" style={{ height: '500px' }}>
                <MapContainer
                  center={
                    zonePolygon.length > 0
                      ? [zonePolygon[0].lat, zonePolygon[0].lng]
                      : DEFAULT_CENTER
                  }
                  zoom={12}
                  style={{ height: '100%', width: '100%' }}
                  scrollWheelZoom
                >
                  <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution='&copy; OpenStreetMap'
                  />
                  <MapClickHandler onMapClick={handleMapClick} drawing={(drawing || testMode) && !deleteMode} />
                  {zonePolygon.length >= 3 && (
                    <Polygon
                      positions={zonePolygon.map(p => [p.lat, p.lng])}
                      pathOptions={{ color: '#0d9488', fillColor: '#14b8a6', fillOpacity: 0.25, weight: 2 }}
                    />
                  )}
                  {zonePolygon.map((p, idx) => (
                    <Marker
                      key={`pt-${idx}`}
                      position={[p.lat, p.lng]}
                      icon={deleteMode ? deletePointIcon : pointIcon}
                      eventHandlers={{
                        click: () => handlePointClick(idx),
                      }}
                    />
                  ))}
                  {testPoint && (
                    <Marker position={[testPoint.lat, testPoint.lng]} icon={testPointIcon} />
                  )}
                  <FitToPolygon polygon={zonePolygon} />
                </MapContainer>
              </div>
              <p className="text-xs text-zinc-500 mt-2">
                {drawing && !testMode && !deleteMode && 'Xəritədə kliklə poligon nöqtələri əlavə edin.'}
                {deleteMode && 'Silmək istədiyiniz nöqtənin üstünə kliklə.'}
                {testMode && 'Test üçün xəritədə hər hansı nöqtəyə kliklə.'}
                {!drawing && !testMode && !deleteMode && 'Mövcud poligon. "Çək" düyməsi ilə yenidən redaktə edə bilərsiniz.'}
              </p>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>
              <X className="h-4 w-4 mr-1" />
              Ləğv et
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="save-zone-btn">
              <Save className="h-4 w-4 mr-1" />
              {saving ? 'Saxlanılır...' : (editingZone ? 'Yenilə' : 'Yarat')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
